import { DEFAULT_DATE_FORMAT } from './date_formats';
import { normalizeDateFormat } from './normalize_date_format';

export interface ResolvedDateFormat {
  /** BCP-47 locale, or undefined when the pattern's field order can't be mapped. */
  locale?: string;
  hourCycle?: 12 | 24;
  /** Separator between date fields ('/', '-', '.'), for restoring it over the locale's. */
  separator?: string;
}

type DateFieldOrder = 'y' | 'M' | 'd';

// English locales chosen so the field order matches the pattern while month
// names and other react-aria labels stay in English.
const LOCALE_BY_ORDER: Record<string, string> = {
  'M,d,y': 'en-US',
  'd,M,y': 'en-GB',
  'y,M,d': 'en-CA',
};

const DATE_FIELDS: DateFieldOrder[] = ['y', 'M', 'd'];

function findSeparator(pattern: string, fieldChar: string): string | undefined {
  let index = pattern.indexOf(fieldChar);
  if (index === -1) {
    return undefined;
  }
  while (index < pattern.length && pattern[index] === fieldChar) {
    index += 1;
  }
  while (index < pattern.length) {
    const character = pattern[index];
    if (!/[a-zA-Z\s]/.test(character)) {
      return character;
    }
    index += 1;
  }
  return undefined;
}

/**
 * Map an Iconik date-fns pattern to the closest react-aria inputs: a BCP-47
 * `locale` (which drives editable segment order) and an `hourCycle`. Only the
 * field order is honored; separators follow the locale, so `MM-dd-yyyy` keeps
 * its order but renders with `/`. Patterns whose order can't be mapped return
 * an undefined `locale` so callers can fall back to the browser locale rather
 * than force one. Read-only display via `formatLocalDate` stays exact.
 */
export function resolveDateFormat(
  pattern: string | null | undefined,
): ResolvedDateFormat {
  const effectivePattern = pattern?.trim() || DEFAULT_DATE_FORMAT;
  // Normalize legacy tokens like the app slice does (the selectors don't), and
  // fold the stand-alone month token `L` into `M` for order detection.
  const normalized = normalizeDateFormat(effectivePattern).replace(/L/g, 'M');
  const withoutLiterals = normalized.replace(/'[^']*'/g, '');

  let hourCycle: 12 | 24 | undefined;
  if (/[hK]/.test(withoutLiterals)) {
    hourCycle = 12;
  } else if (/[Hk]/.test(withoutLiterals)) {
    hourCycle = 24;
  }

  const order = DATE_FIELDS.map((field) => ({
    field,
    index: withoutLiterals.indexOf(field),
  }))
    .filter((entry) => entry.index !== -1)
    .sort((left, right) => left.index - right.index)
    .map((entry) => entry.field);

  const locale = LOCALE_BY_ORDER[order.join(',')];
  const separator =
    order.length > 0 ? findSeparator(withoutLiterals, order[0]) : undefined;

  return { locale, hourCycle, separator };
}
